import api from './api';

// WhatsApp Message Types
export interface WhatsAppMessage {
  id: number;
  phoneNumber: string;
  messageContent: string;
  direction: 'INCOMING' | 'OUTGOING';
  isRead: boolean;
  user?: {
    id: number;
    name: string;
  };
  respondedBy?: {
    id: number;
    name: string;
  };
  timestamp: string;
  twilioMessageSid?: string;
}

export interface WhatsAppConversation {
  id: number;
  phoneNumber: string;
  messageContent: string;
  direction: 'INCOMING' | 'OUTGOING';
  isRead: boolean;
  user?: {
    id: number;
    name: string;
  };
  timestamp: string;
}

export interface ConversationsResponse {
  content: WhatsAppConversation[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  size: number;
  unreadCount: number;
}

export interface ConversationMessagesResponse {
  phoneNumber: string;
  messageCount: number;
  messages: WhatsAppMessage[];
}

export interface UnreadMessagesResponse {
  messages: WhatsAppMessage[];
  count: number;
}

export interface WhatsAppStats {
  unreadCount: number;
  totalConversations: number;
}

export interface SendWhatsAppRequest {
  phoneNumber: string;
  message: string;
}

export interface SendWhatsAppResponse {
  success: boolean;
  message: string;
}

export const whatsappApi = {
  // Get all conversations (paginated)
  getConversations: async (page = 0, size = 20): Promise<ConversationsResponse> => {
    const response = await api.get('/admin/whatsapp/conversations', {
      params: { page, size },
    });
    return response.data;
  },

  // Get conversation messages by phone number (automatically marks as read)
  getConversation: async (phoneNumber: string): Promise<ConversationMessagesResponse> => {
    const response = await api.get(`/admin/whatsapp/conversations/${encodeURIComponent(phoneNumber)}`);
    // Handle both wrapped and direct response formats
    const data = response.data?.data || response.data;
    return data;
  },

  // Send WhatsApp message
  sendMessage: async (data: SendWhatsAppRequest): Promise<SendWhatsAppResponse> => {
    const response = await api.post('/admin/whatsapp/send', {
      phoneNumber: data.phoneNumber,
      message: data.message,
    });
    return response.data;
  },

  // Get unread messages
  getUnread: async (): Promise<UnreadMessagesResponse> => {
    const response = await api.get('/admin/whatsapp/unread');
    return response.data;
  },

  // Mark message as read
  markAsRead: async (messageId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/admin/whatsapp/messages/${messageId}/read`);
    return response.data;
  },

  // Get statistics
  getStats: async (): Promise<WhatsAppStats> => {
    const response = await api.get('/admin/whatsapp/stats');
    return response.data;
  },
};
